/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package models

import kotlinx.serialization.Serializable

/**
 * Local Transaction model for SQLDelight database
 * Maps to the transactions table in the database schema
 */
/**
 * Type of child transaction (for split/distribute)
 */
@Serializable
enum class ChildType {
    SPLIT,
    DISTRIBUTED
}

@Serializable
data class Transaction(
    val transactionId: String,
    val accountId: String,
    val userId: String,
    val amount: Double,
    val isoCurrencyCode: String? = null,
    val unofficialCurrencyCode: String? = null,
    val date: String,
    val authorizedDate: String? = null,
    val authorizedDatetime: String? = null,
    val datetime: String? = null,
    val name: String,
    val merchantName: String? = null,
    val originalDescription: String? = null,
    val category: String? = null, // JSON array string
    val categoryId: String? = null,
    val personalFinanceCategoryPrimary: String? = null,
    val personalFinanceCategoryDetailed: String? = null,
    val personalFinanceCategoryConfidence: String? = null,
    val transactionType: String? = null,
    val transactionCode: String? = null,
    val pending: Boolean = false,
    val pendingTransactionId: String? = null,
    val locationJson: String? = null, // JSON string
    val paymentMetaJson: String? = null, // JSON string
    val accountOwner: String? = null,
    val userCategory: String? = null,
    val userNotes: String? = null,
    val isHidden: Boolean = false,
    val isExpenseReport: Boolean = false,
    val tags: String? = null, // JSON array string
    val createdAt: String? = null,
    val updatedAt: String? = null,
    val lastSyncedAt: String? = null,
    // Split/Distribute transaction fields
    val parentTransactionId: String? = null,
    val childType: ChildType? = null,
    val isSplitParent: Boolean = false,
    val isDistributedParent: Boolean = false,
    val splitOrder: Int? = null,
    val distributionStartMonth: String? = null,
    val distributionEndMonth: String? = null,
    val distributionMonthIndex: Int? = null,
    val distributionTotalMonths: Int? = null,
    // Excluded from calculations flag
    val isExcluded: Boolean = false,
    // Context assignment
    val contextIds: List<String> = emptyList()
) {
    /**
     * Get the effective currency code
     */
    val currencyCode: String
        get() = isoCurrencyCode ?: unofficialCurrencyCode ?: "USD"

    /**
     * Whether this transaction should be excluded from totals.
     * Parent transactions of splits/distributes are excluded because
     * their children represent the actual amounts.
     */
    val excludeFromTotals: Boolean
        get() = TransactionEligibility.excludeFromTotals(isSplitParent, isDistributedParent, isExcluded)

    /**
     * Whether this transaction is an internal money-movement (a savings
     * transfer or a credit-card payment) that must NOT count as income or
     * spending. Single source of truth: [PlaidCategories.isTransfer].
     *
     * Deliberately SEPARATE from [excludeFromTotals]: transfers must still
     * appear in transaction lists, and an assigned savings transfer must
     * still feed the Savings / goal-allocation bar — only the income and
     * expense (cash-flow) aggregations exclude it.
     */
    val isTransfer: Boolean
        get() = PlaidCategories.isTransfer(personalFinanceCategoryPrimary, personalFinanceCategoryDetailed)

    /**
     * Whether this is a credit-card payment. When such a row is [isExcluded]
     * it was auto-excluded as a tracked pair (both legs synced), not manually
     * excluded by the user — the UI shows an "Auto-excluded" badge for it, and
     * the row's Include toggle lets the user override.
     * Single source of truth: [PlaidCategories.isCreditCardPayment].
     */
    val isCreditCardPayment: Boolean
        get() = PlaidCategories.isCreditCardPayment(personalFinanceCategoryPrimary, personalFinanceCategoryDetailed)

    /**
     * Whether this transaction is a child of another transaction
     */
    val isChildTransaction: Boolean
        get() = parentTransactionId != null

    /**
     * Whether this transaction is a split child
     */
    val isSplitChild: Boolean
        get() = childType == ChildType.SPLIT

    /**
     * Whether this transaction is a distributed child
     */
    val isDistributedChild: Boolean
        get() = childType == ChildType.DISTRIBUTED

    /**
     * Whether this is a combined parent (both split and distributed)
     */
    val isCombinedParent: Boolean
        get() = isSplitParent && isDistributedParent

    // --- Action Availability ---

    /** Normal transactions and distributed children can be split */
    val canSplit: Boolean
        get() = when {
            isSplitParent || isDistributedParent -> false  // parents can't be split
            childType == ChildType.SPLIT -> false           // split children can't be split again
            childType == ChildType.DISTRIBUTED -> true      // distributed children CAN be split
            else -> true                                     // normal transactions can be split
        }

    /** Normal transactions and split children can be distributed */
    val canDistribute: Boolean
        get() = when {
            isDistributedParent -> false                    // already distributed
            childType == ChildType.DISTRIBUTED -> false     // distributed children can't be distributed
            childType == ChildType.SPLIT -> true            // split children CAN be distributed
            isSplitParent -> true                           // split parents CAN be distributed
            else -> true                                     // normal transactions
        }

    /** Only top-level transactions can be excluded (parents or normal) */
    val canExclude: Boolean
        get() = parentTransactionId == null

    /** Only split parents can be unsplit */
    val canUnsplit: Boolean
        get() = isSplitParent

    /** Only distributed parents can be undistributed */
    val canUndistribute: Boolean
        get() = isDistributedParent

    /** Only children can have their category changed */
    val canChangeCategory: Boolean
        get() = parentTransactionId != null || (!isSplitParent && !isDistributedParent)

    // Backward compatibility properties for Android app
    val categoryLevel1: String
        get() = personalFinanceCategoryPrimary ?: ""

    val categoryLevel2: String
        get() = personalFinanceCategoryDetailed ?: ""

    val categoryLevel3: String
        get() = personalFinanceCategoryConfidence ?: ""

    val merchant_name: String?
        get() = merchantName

    val personal_finance_category: models.PersonalFinanceCategory?
        get() = if (personalFinanceCategoryPrimary != null && personalFinanceCategoryDetailed != null) {
            models.PersonalFinanceCategory(
                primary = personalFinanceCategoryPrimary,
                detailed = personalFinanceCategoryDetailed,
                confidenceLevel = personalFinanceCategoryConfidence
            )
        } else null

    /**
     * Get the effective display category with Rent/Mortgage vs Utilities split.
     * Maps Plaid detailed categories to refined display categories.
     */
    val effectiveDisplayCategory: String
        get() = getEffectiveDisplayCategory(
            personalFinanceCategoryPrimary ?: category ?: "Other",
            personalFinanceCategoryDetailed ?: personalFinanceCategoryPrimary ?: category ?: "Other"
        )

    companion object {
        /**
         * Map Plaid detailed category to refined display category.
         * Splits RENT_AND_UTILITIES into separate categories:
         * - Rent/Mortgage: Rent and mortgage payments
         * - Utilities: Gas, electricity, internet, phone, water, etc.
         * - Subscriptions: Streaming services, software subscriptions (from ENTERTAINMENT)
         */
        fun getEffectiveDisplayCategory(primary: String, detailed: String): String {
            val primaryUpper = primary.uppercase()
            val detailedUpper = detailed.uppercase()

            return when {
                // Split RENT_AND_UTILITIES into Rent/Mortgage vs Utilities
                primaryUpper == "RENT_AND_UTILITIES" -> {
                    when {
                        // Check for specific RENT detailed category
                        // Not just contains("RENT") which matches "RENT_AND_UTILITIES" itself
                        detailedUpper == "RENT_AND_UTILITIES_RENT" ||
                        detailedUpper.endsWith("_RENT") ||
                        (detailedUpper.contains("RENT") && detailedUpper != "RENT_AND_UTILITIES" && !detailedUpper.contains("UTILITIES")) -> "Rent/Mortgage"
                        // Specific utility categories
                        detailedUpper.contains("GAS_AND_ELECTRICITY") ||
                        detailedUpper.contains("INTERNET") ||
                        detailedUpper.contains("CABLE") ||
                        detailedUpper.contains("TELEPHONE") ||
                        detailedUpper.contains("WATER") ||
                        detailedUpper.contains("SEWAGE") ||
                        detailedUpper.contains("OTHER_UTILITIES") -> "Utilities"
                        // If detailed equals primary (no detailed info), default to Utilities
                        else -> "Utilities"
                    }
                }
                // Mortgage payments in LOAN_PAYMENTS should go to Rent/Mortgage
                primaryUpper == "LOAN_PAYMENTS" && detailedUpper.contains("MORTGAGE") -> "Rent/Mortgage"
                // Insurance in GENERAL_SERVICES stays as Insurance for clarity
                primaryUpper == "GENERAL_SERVICES" && detailedUpper.contains("INSURANCE") -> "Insurance"
                // Streaming services are Subscriptions, not Entertainment
                // Plaid categorizes Netflix, Spotify, etc. as ENTERTAINMENT_MUSIC_AND_AUDIO or ENTERTAINMENT_TV_AND_MOVIES
                primaryUpper == "ENTERTAINMENT" && (
                    detailedUpper.contains("MUSIC_AND_AUDIO") ||
                    detailedUpper.contains("TV_AND_MOVIES") ||
                    detailedUpper.contains("VIDEO_GAMES")
                ) -> "Subscriptions"
                // Map other Plaid categories to display names
                else -> getCategoryDisplayName(primary)
            }
        }

        /**
         * Map a budget plan's categoryName to a display category (English).
         * Budget plans store primary codes ("FOOD_AND_DRINK"), detailed codes
         * ("RENT_AND_UTILITIES_RENT"), or "CUSTOM:" prefixed names.
         * Extracts the primary code from detailed codes so getEffectiveDisplayCategory
         * can properly split categories like RENT_AND_UTILITIES.
         */
        fun mapBudgetCategoryToDisplayCategory(budgetCategory: String): String {
            if (budgetCategory.startsWith("CUSTOM:", ignoreCase = true)) {
                return budgetCategory.substringAfter(":")
            }

            val upper = budgetCategory.uppercase()
            val knownPrimaries = listOf(
                "GOVERNMENT_AND_NON_PROFIT", "GENERAL_MERCHANDISE",
                "RENT_AND_UTILITIES", "HOME_IMPROVEMENT", "GENERAL_SERVICES",
                "FOOD_AND_DRINK", "TRANSPORTATION", "PERSONAL_CARE",
                "LOAN_PAYMENTS", "ENTERTAINMENT", "TRANSFER_OUT",
                "TRANSFER_IN", "BANK_FEES", "MEDICAL", "TRAVEL", "INCOME"
            )

            for (primary in knownPrimaries) {
                if (upper == primary || upper.startsWith("${primary}_")) {
                    return getEffectiveDisplayCategory(primary, upper)
                }
            }

            return getCategoryDisplayName(upper)
        }

        /**
         * Map Plaid category codes to user-friendly display names.
         */
        fun getCategoryDisplayName(category: String): String {
            return when (category.uppercase()) {
                "FOOD_AND_DRINK" -> "Food & Drink"
                "TRANSPORTATION" -> "Transportation"
                "GENERAL_MERCHANDISE" -> "Shopping"
                "ENTERTAINMENT" -> "Entertainment"
                "RENT_MORTGAGE" -> "Rent/Mortgage"
                "UTILITIES" -> "Utilities"
                "SUBSCRIPTIONS" -> "Subscriptions"
                "INSURANCE" -> "Insurance"
                "RENT_AND_UTILITIES" -> "Rent & Utilities" // Legacy fallback
                "MEDICAL" -> "Healthcare"
                "PERSONAL_CARE" -> "Personal Care"
                "TRAVEL" -> "Travel"
                "GENERAL_SERVICES" -> "Services"
                "BANK_FEES" -> "Bank Fees"
                "TRANSFER_OUT" -> "Transfers Out"
                "TRANSFER_IN" -> "Transfers In"
                "LOAN_PAYMENTS" -> "Loan Payments"
                "INCOME" -> "Income"
                "HOME_IMPROVEMENT" -> "Home"
                "GOVERNMENT_AND_NON_PROFIT" -> "Government"
                else -> category.replace("_", " ").lowercase().replaceFirstChar { it.uppercase() }
            }
        }
    }
}

/**
 * Local Account model for SQLDelight database  
 * Maps to the accounts table in the database schema
 */
@Serializable
data class LocalAccount(
    val accountId: String,
    val userId: String,
    val itemId: String,
    val name: String,
    val officialName: String? = null,
    val mask: String? = null,
    val type: String,
    val subtype: String? = null,
    val balanceAvailable: Double? = null,
    val balanceCurrent: Double? = null,
    val balanceLimit: Double? = null,
    val isoCurrencyCode: String? = null,
    val unofficialCurrencyCode: String? = null,
    val balanceLastUpdated: String? = null,
    val verificationStatus: String? = null,
    val persistentAccountId: String? = null,
    val createdAt: String? = null,
    val updatedAt: String? = null,
    val lastSyncedAt: String? = null
) {
    /**
     * Get the effective currency code
     */
    val currencyCode: String
        get() = isoCurrencyCode ?: unofficialCurrencyCode ?: "USD"
        
    /**
     * Get the display balance (prefer current over available)
     */
    val displayBalance: Double
        get() = balanceCurrent ?: balanceAvailable ?: 0.0
}

/**
 * Local PlaidLinkToken model for SQLDelight database
 * Maps to the plaid_tokens table in the database schema
 * Note: This is simpler than the full PlaidLinkToken in PlaidToken.kt
 */
@Serializable
data class LocalPlaidToken(
    val userId: String,
    val linkToken: String? = null,
    val linkTokenExpiration: String? = null,
    val publicToken: String? = null,
    val accessToken: String,
    val itemId: String,
    val institutionId: String? = null,
    val institutionName: String? = null,
    val state: String = "CREATED",
    val securityLevel: String = "ACCESS",
    val createdAt: String? = null,
    val updatedAt: String? = null,
    val lastUsedAt: String? = null,
    val errorCode: String? = null,
    val errorMessage: String? = null,
    val webhookUrl: String? = null,
    val metadata: String? = null // JSON string
)

/**
 * Represents a single item in a split transaction
 */
@Serializable
data class SplitItem(
    val amount: Double,
    val category: String,
    val detailedCategory: String? = null,
    val note: String? = null
)

/**
 * Represents a distributed amount for a specific month
 */
@Serializable
data class DistributedItem(
    val month: String, // "2025-01" format
    val amount: Double
)

/**
 * Result of parsing a CSV for split items
 */
@Serializable
data class CSVParseResult(
    val items: List<SplitItem>,
    val parseErrors: List<String>
)

/**
 * Request to split a transaction
 */
@Serializable
data class SplitTransactionRequest(
    val parentTransactionId: String,
    val splits: List<SplitItem>
)

/**
 * Request to distribute a transaction across months
 */
@Serializable
data class DistributeTransactionRequest(
    val parentTransactionId: String,
    val startMonth: String, // "2025-01" format
    val endMonth: String    // "2025-06" format
)